import { useState, useCallback, useRef, useEffect } from 'react'
import { useIntl } from 'react-intl'
import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk'
import { makeGetRequest } from '../../../../../_state/helpers/api'
import { configWithAuth } from '../../../../../_state/helpers'
import { playerMessages } from '../CapeeshPlayer.i18n.Messages'

const TOKEN_REFRESH_INTERVAL_MS = 9 * 60 * 1000
const TOKEN_MAX_AGE_MS = 8 * 60 * 1000
const CLEANUP_TIMEOUT_MS = 3000
const isDev = process.env.NODE_ENV !== 'production'

// Shared across hook instances so a new recognizer waits for the previous
// one's stop + close to finish before acquiring the microphone.
let prevCleanupPromise = Promise.resolve()

function cleanupRecognizer(recognizer) {
    if (!recognizer) return
    prevCleanupPromise = new Promise((resolve) => {
        const timeout = setTimeout(() => {
            if (isDev) console.warn('[AzureSpeech] Cleanup timed out — forcing resolve')
            resolve()
        }, CLEANUP_TIMEOUT_MS)

        const finish = () => {
            clearTimeout(timeout)
            resolve()
        }

        try {
            recognizer.stopContinuousRecognitionAsync(
                () => { try { recognizer.close() } catch {} finally { finish() } },
                () => { try { recognizer.close() } catch {} finally { finish() } }
            )
        } catch {
            try { recognizer.close() } catch {}
            finish()
        }
    })
}

/**
 * useAzureSpeechRecognition — manages Azure Speech-to-Text streaming recognition.
 *
 * Fetches short-lived authorization tokens from the backend, connects to Azure,
 * captures microphone input, and provides real-time recognition callbacks.
 * Automatically refreshes the token before it expires (every ~9 min).
 *
 * @param {number} l2LanguageId — the L2 language ID used to look up the correct Azure language config
 * @returns {object} — { startRecognition, stopRecognition, isRecording, error, hasToken, fetchToken }
 */
export default function useAzureSpeechRecognition(l2LanguageId) {
    const intl = useIntl()
    const [isRecording, setIsRecording] = useState(false)
    const [error, setError] = useState(null)
    const [hasToken, setHasToken] = useState(false)

    const recognizerRef = useRef(null)
    const tokenDataRef = useRef(null)
    const tokenFetchedAtRef = useRef(0)
    const refreshIntervalRef = useRef(null)

    useEffect(() => {
        return () => {
            clearTokenRefreshInterval()
            disposeRecognizer()
        }
    }, [])

    const clearTokenRefreshInterval = useCallback(() => {
        if (refreshIntervalRef.current) {
            clearInterval(refreshIntervalRef.current)
            refreshIntervalRef.current = null
        }
    }, [])

    const disposeRecognizer = useCallback(() => {
        const recognizer = recognizerRef.current
        recognizerRef.current = null
        setIsRecording(false)
        cleanupRecognizer(recognizer)
    }, [])

    const isTokenStale = useCallback(() => {
        return !tokenDataRef.current || (Date.now() - tokenFetchedAtRef.current) > TOKEN_MAX_AGE_MS
    }, [])

    const fetchToken = useCallback(async () => {
        try {
            const response = await makeGetRequest('speech/recognition/token/v1', configWithAuth())
            const tokenData = response.data

            if (!Array.isArray(tokenData) || tokenData.length === 0) {
                throw new Error('No speech recognition token data returned')
            }

            tokenDataRef.current = tokenData
            tokenFetchedAtRef.current = Date.now()
            setHasToken(true)
            return tokenData
        } catch (err) {
            console.error('[useAzureSpeechRecognition] Failed to fetch token:', err)
            setError(intl.formatMessage(playerMessages.speechCredentialsError))
            return null
        }
    }, [intl])

    const refreshTokenOnRecognizer = useCallback(async () => {
        const freshTokenData = await fetchToken()
        if (!freshTokenData || !recognizerRef.current) return

        const langToken = freshTokenData.find((c) => c.languageId === l2LanguageId) || freshTokenData[0]
        if (langToken?.authorizationToken) {
            recognizerRef.current.authorizationToken = langToken.authorizationToken
        }
    }, [fetchToken, l2LanguageId])

    const startTokenRefreshInterval = useCallback(() => {
        clearTokenRefreshInterval()
        refreshIntervalRef.current = setInterval(refreshTokenOnRecognizer, TOKEN_REFRESH_INTERVAL_MS)
    }, [clearTokenRefreshInterval, refreshTokenOnRecognizer])

    /**
     * Start continuous speech recognition.
     *
     * @param {object} callbacks
     * @param {function} callbacks.onRecognizing — called with interim results: (text: string)
     * @param {function} callbacks.onRecognized — called with final results: (text: string)
     * @param {function} callbacks.onError — called on error: (errorMessage: string)
     * @param {function} callbacks.onSessionStopped — called when the session ends
     */
    const startRecognition = useCallback(
        async ({ onRecognizing, onRecognized, onError, onSessionStopped } = {}) => {
            setError(null)

            if (recognizerRef.current) {
                disposeRecognizer()
            }

            // Wait for any previous recognizer (including from a prior hook
            // instance / node) to finish its stop + close before we acquire
            // the microphone, avoiding browser audio-hardware races.
            await prevCleanupPromise

            let tokenData = tokenDataRef.current
            if (!tokenData || isTokenStale()) {
                tokenData = await fetchToken()
            }
            if (!tokenData) {
                onError?.('No speech recognition token available')
                return false
            }

            const langToken = tokenData.find((c) => c.languageId === l2LanguageId) || tokenData[0]

            if (!langToken?.authorizationToken || !langToken?.serviceRegion) {
                setError('Invalid speech recognition token')
                onError?.('Invalid speech recognition token')
                return false
            }

            try {
                const speechConfig = SpeechSDK.SpeechConfig.fromAuthorizationToken(
                    langToken.authorizationToken,
                    langToken.serviceRegion
                )

                if (langToken.speechRecognitionLanguageTag) {
                    speechConfig.speechRecognitionLanguage = langToken.speechRecognitionLanguageTag
                }

                const audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput()
                const recognizer = new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig)

                recognizer.recognizing = (_sender, e) => {
                    if (isDev) console.log('[AzureSpeech] recognizing:', e.result?.text, '| reason:', e.result?.reason)
                    if (e.result?.text) {
                        onRecognizing?.(e.result.text)
                    }
                }

                recognizer.recognized = (_sender, e) => {
                    if (isDev) console.log('[AzureSpeech] recognized:', e.result?.text, '| reason:', e.result?.reason)
                    if (e.result?.reason === SpeechSDK.ResultReason.RecognizedSpeech && e.result?.text) {
                        onRecognized?.(e.result.text)
                    }
                }

                recognizer.canceled = (_sender, e) => {
                    if (isDev) console.log('[AzureSpeech] canceled:', { reason: e.reason, errorCode: e.errorCode, errorDetails: e.errorDetails })
                    if (e.reason === SpeechSDK.CancellationReason.Error) {
                        console.error('[AzureSpeech] Canceled:', e.errorDetails)
                        setError(e.errorDetails || 'Speech recognition canceled')
                        onError?.(e.errorDetails || 'Speech recognition was canceled')
                    }
                    setIsRecording(false)
                }

                recognizer.sessionStopped = (_sender, e) => {
                    if (isDev) console.log('[AzureSpeech] sessionStopped:', e)
                    setIsRecording(false)
                    onSessionStopped?.()
                }

                recognizerRef.current = recognizer
                startTokenRefreshInterval()

                return new Promise((resolve) => {
                    recognizer.startContinuousRecognitionAsync(
                        () => {
                            setIsRecording(true)
                            resolve(true)
                        },
                        (err) => {
                            console.error('[AzureSpeech] Start failed:', err)
                            setError('Failed to start speech recognition')
                            onError?.('Failed to start speech recognition')
                            setIsRecording(false)
                            clearTokenRefreshInterval()
                            resolve(false)
                        }
                    )
                })
            } catch (err) {
                console.error('[AzureSpeech] Setup error:', err)
                setError('Failed to initialize speech recognition')
                onError?.('Failed to initialize speech recognition')
                return false
            }
        },
        [l2LanguageId, fetchToken, isTokenStale, disposeRecognizer, startTokenRefreshInterval, clearTokenRefreshInterval]
    )

    const stopRecognition = useCallback(() => {
        clearTokenRefreshInterval()
        const recognizer = recognizerRef.current
        recognizerRef.current = null
        setIsRecording(false)
        cleanupRecognizer(recognizer)
    }, [clearTokenRefreshInterval])

    return {
        startRecognition,
        stopRecognition,
        isRecording,
        error,
        hasToken,
        fetchToken,
    }
}
